/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// React hooks
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { Card, LinearProgress, Stack } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";

// Mytherra API Service
import mytherraApi from "services/mytherraApi";

// Wallet Services
import walletService from "services/walletService";
import storageService from "services/storageService";
import currencyService from "services/currencyService";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import linearGradient from "assets/theme/functions/linearGradient";

// Vision UI Dashboard React base styles
import typography from "assets/theme/base/typography";
import colors from "assets/theme/base/colors";

// Dashboard layout components
import WelcomeMark from "layouts/dashboard/components/WelcomeMark";
import Projects from "layouts/dashboard/components/Projects";
import OrderOverview from "layouts/dashboard/components/OrderOverview";
import SatisfactionRate from "layouts/dashboard/components/SatisfactionRate";
import ReferralTracking from "layouts/dashboard/components/ReferralTracking";

// React icons
import { IoIosRocket } from "react-icons/io";
import { IoGlobe } from "react-icons/io5";
import { IoBuild } from "react-icons/io5";
import { IoWallet } from "react-icons/io5";
import { IoDocumentText } from "react-icons/io5";
import { FaShoppingCart } from "react-icons/fa";

// Data
import LineChart from "examples/Charts/LineCharts/LineChart";
import BarChart from "examples/Charts/BarCharts/BarChart";
import { lineChartDataDashboard } from "layouts/dashboard/data/lineChartData";
import { lineChartOptionsDashboard } from "layouts/dashboard/data/lineChartOptions";
import { barChartDataDashboard } from "layouts/dashboard/data/barChartData";
import { barChartOptionsDashboard } from "layouts/dashboard/data/barChartOptions";

function Dashboard() {
  const history = useHistory();
  const { gradients } = colors;
  const { cardContent } = gradients;

  // Wallet state
  const [walletAddress, setWalletAddress] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletUtxos, setWalletUtxos] = useState([]);
  const [walletLoading, setWalletLoading] = useState(true);

  // State for blockchain data
  const [networkStats, setNetworkStats] = useState({
    blockHeight: "Loading...",
    networkPeers: 0,
    status: "Connecting...",
    difficulty: 0,
    hashrate: 0,
    moneySupply: "Loading..."
  });

  const [loading, setLoading] = useState(true);
  
  // State for latest blocks
  const [latestBlocks, setLatestBlocks] = useState([]);
  
  // State for wallet transactions
  const [transactions, setTransactions] = useState([]);
  
  // State for chart data
  const [balanceChartData, setBalanceChartData] = useState([]);
  const [balanceChartOptions, setBalanceChartOptions] = useState({});
  
  // State for MYT price
  const [mytPrice, setMytPrice] = useState(null);

  // RPC Configuration
  const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3002' : '/api';
  const RPC_URL = `${API_BASE}/rpc`;
  const RPC_USER = '';
  const RPC_PASSWORD = '';

  // Load wallet data
  useEffect(() => {
    const loadWallet = async () => {
      try {
        setWalletLoading(true);

        // Check if user has a session
        const privateKey = storageService.loadSession();
        
        if (!privateKey) {
          // No wallet loaded, redirect to sign in
          console.log('No wallet session found, redirecting to sign in');
          history.push('/sign-in');
          return;
        }

        // Get address from private key
        const address = await walletService.getAddressFromWif(privateKey);
        
        if (!address) {
          console.error('Invalid private key in session');
          storageService.clearSession();
          history.push('/sign-in');
          return;
        }

        setWalletAddress(address);

        // Load UTXOs and calculate balance
        const utxos = await walletService.getUtxos(RPC_URL, RPC_USER, RPC_PASSWORD, address);
        setWalletUtxos(utxos);
        
        const balance = walletService.calculateBalance(utxos);
        setWalletBalance(balance);

        // If no UTXOs from RPC, try Explorer for balance
        if (utxos.length === 0) {
          const explorerBalance = await walletService.getBalanceFromExplorer(address);
          if (explorerBalance !== null) {
            setWalletBalance(explorerBalance);
          }
        }

        setWalletLoading(false);
      } catch (error) {
        console.error('Error loading wallet:', error);
        setWalletLoading(false);
      }
    };

    loadWallet();
  }, [history]);

  // Fetch blockchain data and latest blocks
  useEffect(() => {
    const fetchBlockchainData = async () => {
      try {
        // Only show loading on first load, not on refresh
        if (networkStats.blockHeight === "Loading...") {
          setLoading(true);
        }
        
        // Fetch money supply separately from dedicated endpoint
        const moneySupplyPromise = mytherraApi.getMoneySupply();
        
        // Try to fetch combined dashboard data (RPC + Explorer)
        const dashboardData = await mytherraApi.getDashboardData();
        
        // Wait for money supply
        const supply = await moneySupplyPromise;
        
        if (dashboardData && dashboardData.explorer) {
          // Use explorer data if available
          const explorerStats = dashboardData.explorer;
          
          setNetworkStats({
            blockHeight: (explorerStats.blockHeight || dashboardData.rpc?.blockCount || 0).toLocaleString(),
            networkPeers: explorerStats.connections || 0,
            status: explorerStats.status === 'online' ? 'Online' : 'Offline',
            difficulty: explorerStats.difficulty || 0,
            hashrate: explorerStats.networkhashps || 0,
            moneySupply: supply ? supply.toFixed(0) : "N/A"
          });
        } else {
          // Fallback to RPC-only data
          const stats = await mytherraApi.getNetworkStatus();
          
          if (stats) {
            setNetworkStats({
              blockHeight: stats.blockHeight?.toLocaleString() || "N/A",
              networkPeers: stats.networkPeers || 0,
              status: stats.status || "Offline",
              difficulty: stats.difficulty || 0,
              hashrate: stats.hashrate || 0,
              moneySupply: supply ? supply.toFixed(0) : "N/A"
            });
          }
        }
        
        // Fetch latest blocks
        await fetchLatestBlocks();
        
        // Fetch wallet transactions
        await fetchTransactions();
        
        // Fetch MYT price
        await fetchPrice();
        
        // Only set loading false on first load
        if (loading) {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching blockchain data:", error);
        setLoading(false);
      }
    };

    // Fetch latest blocks from RPC
    const fetchLatestBlocks = async () => {
      try {
        const blockCount = await mytherraApi.getBlockCount();
        if (!blockCount) return;

        const blocks = [];
        // Get last 3 blocks
        for (let i = 0; i < 3; i++) {
          const height = blockCount - i;
          const hash = await mytherraApi.getBlockHash(height);
          if (hash) {
            const block = await mytherraApi.getBlock(hash);
            if (block) {
              blocks.push({
                height: height,
                hash: hash,
                time: block.time,
                txCount: block.tx ? block.tx.length : 0
              });
            }
          }
        }
        
        setLatestBlocks(blocks);
      } catch (error) {
        console.error("Error fetching latest blocks:", error);
      }
    };

    // Fetch MYT price
    const fetchPrice = async () => {
      try {
        const response = await fetch(`${API_BASE}/price`);
        if (response.ok) {
          const data = await response.json();
          setMytPrice(data.price);
        }
      } catch (err) {
        console.log('Price fetch failed:', err);
      }
    };

    // Fetch wallet transactions for this specific address from Explorer
    const fetchTransactions = async () => {
      try {
        const privateKey = storageService.loadSession();
        if (privateKey) {
          const address = await walletService.getAddressFromWif(privateKey);
          if (address) {
            // Try Explorer API first
            try {
              const addressData = await mytherraApi.getExplorerAddress(address);
              console.log('Dashboard - Address data from explorer:', addressData);
              
              if (addressData && addressData.txs && Array.isArray(addressData.txs) && addressData.txs.length > 0) {
                // txs is an array of transaction IDs, fetch details via Explorer API
                const txDetails = await Promise.all(
                  addressData.txs.slice(0, 10).map(async (txid) => {
                    try {
                      // Use Explorer API via proxy to get transaction details
                      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                      const API_BASE = isLocalhost ? 'http://localhost:3002' : '/api';
                      
                      const response = await fetch(`${API_BASE}/explorer/api/getrawtransaction?txid=${txid}&decrypt=1`);
                      if (!response.ok) {
                        console.log(`Failed to fetch tx ${txid}: ${response.status}`);
                        return null;
                      }
                      
                      const txData = await response.json();
                      if (!txData || txData.error) {
                        console.log(`Error in tx data for ${txid}:`, txData?.error);
                        return null;
                      }
                      
                      // Determine if this is a send or receive
                      // A tx is a send if it's in the address's input_txs (we spent inputs)
                      const isInputTx = addressData.input_txs && addressData.input_txs.includes(txid);
                      
                      let amount = 0;
                      let category = 'receive';
                      
                      if (isInputTx) {
                        // It's a send: sum all outputs that are NOT to our address
                        category = 'send';
                        for (const vout of txData.vout || []) {
                          if (vout.scriptPubKey && vout.scriptPubKey.address !== address) {
                            amount += vout.value;
                          }
                        }
                        amount = -amount; // negative for send
                      } else {
                        // It's a receive: sum outputs to our address
                        for (const vout of txData.vout || []) {
                          if (vout.scriptPubKey && vout.scriptPubKey.address === address) {
                            amount += vout.value;
                          }
                        }
                      }
                      
                      // Get timestamp - try multiple sources
                      let timestamp = txData.time || txData.blocktime;
                      
                      // If no timestamp and we have a blockhash, fetch the block to get the time
                      if (!timestamp && txData.blockhash) {
                        try {
                          const blockResponse = await fetch(`${API_BASE}/explorer/api/getblock?hash=${txData.blockhash}`);
                          if (blockResponse.ok) {
                            const blockData = await blockResponse.json();
                            timestamp = blockData.time || blockData.blocktime;
                          }
                        } catch (blockErr) {
                          console.log('Could not fetch block time for', txData.blockhash);
                        }
                      }
                      
                      // Last resort: use current time (but log a warning)
                      if (!timestamp) {
                        console.warn('No timestamp found for tx', txid, '- using current time');
                        timestamp = Date.now() / 1000;
                      }
                      
                      return {
                        txid: txid,
                        amount: amount,
                        category: category,
                        confirmations: txData.confirmations || 0,
                        time: timestamp
                      };
                    } catch (err) {
                      console.log('Could not fetch tx details for', txid, err);
                      return null;
                    }
                  })
                );
                
                // Filter out null values and sort by time
                const txs = txDetails.filter(tx => tx !== null);
                txs.sort((a, b) => b.time - a.time);
                setTransactions(txs);
                
                // Generate chart data
                generateBalanceChart(txs, addressData.balance || 0);
                return;
              }
            } catch (explorerError) {
              console.log('Explorer API not available for this address (possibly Bech32 format not supported), using UTXOs as fallback');
            }
            
            // Fallback: Convert UTXOs to transaction format with real timestamps
            const utxos = await walletService.getUtxos(RPC_URL, RPC_USER, RPC_PASSWORD, address);
            if (utxos && utxos.length > 0) {
              // Fetch transaction details for each UTXO to get timestamps
              const txList = await Promise.all(
                utxos.map(async (utxo) => {
                  try {
                    // Get raw transaction to get timestamp
                    const txDetails = await mytherraApi.rpcCall('getrawtransaction', [utxo.txid, true]);
                    return {
                      txid: utxo.txid,
                      vout: utxo.vout,
                      amount: utxo.amount,
                      confirmations: utxo.confirmations,
                      category: 'receive',
                      time: txDetails?.time || txDetails?.blocktime || Date.now() / 1000
                    };
                  } catch (err) {
                    console.log('Could not fetch tx details for', utxo.txid);
                    return {
                      txid: utxo.txid,
                      vout: utxo.vout,
                      amount: utxo.amount,
                      confirmations: utxo.confirmations,
                      category: 'receive',
                      time: Date.now() / 1000
                    };
                  }
                })
              );
              // Sort by time (newest first)
              txList.sort((a, b) => b.time - a.time);
              setTransactions(txList.slice(0, 10));
            } else {
              setTransactions([]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        setTransactions([]);
      }
    };

    // Initial fetch
    fetchBlockchainData();

    // Refresh every 60 seconds instead of 30 (less flickering)
    const interval = setInterval(fetchBlockchainData, 60000);

    return () => clearInterval(interval);
  }, []);

  // Helper function to format time ago
  const timeAgo = (timestamp) => {
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Generate balance chart from transactions
  const generateBalanceChart = (txs, currentBalance) => {
    if (!txs || txs.length === 0) {
      // No transactions - show flat line at current balance
      setBalanceChartData([{
        name: "Balance",
        data: [currentBalance, currentBalance]
      }]);
      setBalanceChartOptions({
        chart: { toolbar: { show: false } },
        tooltip: { theme: "dark" },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        xaxis: {
          categories: ["Start", "Now"],
          labels: { style: { colors: "#c0c0c0", fontSize: "12px" } }
        },
        yaxis: {
          labels: { style: { colors: "#c0c0c0", fontSize: "12px" } }
        },
        colors: ["#00CED1"],
        grid: { strokeDashArray: 5, borderColor: "#56577A" }
      });
      return;
    }

    // Sort transactions by time (oldest first for balance calculation)
    const sortedTxs = [...txs].sort((a, b) => a.time - b.time);
    
    // Calculate balance at each point (working backwards from current)
    let balance = currentBalance;
    const balancePoints = [];
    const labels = [];
    
    // Add current balance as the last point
    balancePoints.push(balance);
    labels.push(new Date().toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }));
    
    // Work backwards through transactions
    for (let i = sortedTxs.length - 1; i >= 0; i--) {
      const tx = sortedTxs[i];
      // Reverse the transaction to get previous balance
      balance -= tx.amount;
      
      balancePoints.unshift(balance);
      labels.unshift(new Date(tx.time * 1000).toLocaleDateString('de-DE', { 
        month: 'short', 
        day: 'numeric' 
      }));
    }
    
    // Limit to last 10 points for chart readability
    const maxPoints = 10;
    const chartData = balancePoints.slice(-maxPoints);
    const chartLabels = labels.slice(-maxPoints);
    
    setBalanceChartData([{
      name: "Balance",
      data: chartData
    }]);
    
    setBalanceChartOptions({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (val) => `${val.toFixed(2)} MYT`
        }
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        width: 3
      },
      xaxis: {
        categories: chartLabels,
        labels: {
          style: {
            colors: "#c0c0c0",
            fontSize: "12px"
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: "#c0c0c0",
            fontSize: "12px"
          },
          formatter: (val) => val.toFixed(0)
        }
      },
      colors: ["#00CED1"],
      grid: {
        strokeDashArray: 5,
        borderColor: "#56577A"
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          shadeIntensity: 0,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 0.8,
          opacityTo: 0,
          stops: [0, 100]
        }
      }
    });
  };

  // Copy address to clipboard
  const copyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      alert('Address copied to clipboard!');
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Wallet Balance", fontWeight: "regular" }}
                count={walletLoading ? "Loading..." : `${walletBalance.toFixed(8)} MYT`}
                percentage={{ 
                  color: "success", 
                  text: mytPrice 
                    ? `≈ ${currencyService.formatAmount(walletBalance * mytPrice)}`
                    : walletUtxos.length > 0 ? `${walletUtxos.length} UTXOs` : "No UTXOs"
                }}
                icon={{ color: "info", component: <IoWallet size="22px" color="#000000" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Transactions" }}
                count={transactions.length.toString()}
                percentage={{ color: "success", text: transactions.length > 0 ? "Active" : "No TX" }}
                icon={{ color: "info", component: <IoDocumentText size="22px" color="#000000" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Network Status" }}
                count={networkStats.status}
                percentage={{ color: "success", text: `${networkStats.networkPeers} peers` }}
                icon={{ color: "info", component: <IoGlobe size="22px" color="#000000" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Confirmed UTXOs" }}
                count={walletUtxos.length.toString()}
                percentage={{ color: "info", text: walletUtxos.length > 0 ? "Ready to spend" : "None" }}
                icon={{ color: "info", component: <IoIosRocket size="20px" color="#000000" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} lg={12} xl={7}>
              <WelcomeMark />
            </Grid>
            <Grid item xs={12} lg={12} xl={5}>
              <Card>
                <VuiBox p={3}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold" mb={3}>
                    Wallet Address
                  </VuiTypography>
                  {walletLoading ? (
                    <VuiBox display="flex" alignItems="center" justifyContent="center" p={3}>
                      <VuiTypography variant="button" color="text">
                        Loading wallet...
                      </VuiTypography>
                    </VuiBox>
                  ) : walletAddress ? (
                    <>
                      <VuiBox
                        sx={{
                          background: "rgba(0, 206, 209, 0.1)",
                          border: "1px solid rgba(0, 206, 209, 0.3)",
                          borderRadius: "12px",
                          p: 2,
                          mb: 2,
                        }}
                      >
                        <VuiTypography variant="button" color="text" fontWeight="regular" mb={1}>
                          Your MYT Address
                        </VuiTypography>
                        <VuiTypography
                          variant="h6"
                          color="info"
                          fontWeight="bold"
                          sx={{
                            wordBreak: "break-all",
                            fontFamily: "monospace",
                            fontSize: "14px",
                          }}
                        >
                          {walletAddress}
                        </VuiTypography>
                      </VuiBox>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <VuiBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={copyAddress}
                            sx={{
                              background: "rgba(0, 206, 209, 0.05)",
                              borderRadius: "12px",
                              p: 2,
                              cursor: "pointer",
                              "&:hover": {
                                background: "rgba(0, 206, 209, 0.15)",
                              },
                            }}
                          >
                            <Icon sx={{ color: "#00CED1", fontSize: "32px !important", mb: 1 }}>
                              content_copy
                            </Icon>
                            <VuiTypography variant="button" color="white" fontWeight="medium">
                              Copy Address
                            </VuiTypography>
                          </VuiBox>
                        </Grid>
                        <Grid item xs={6}>
                          <VuiBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            onClick={() => history.push('/billing')}
                            sx={{
                              background: "rgba(0, 206, 209, 0.05)",
                              borderRadius: "12px",
                              p: 2,
                              cursor: "pointer",
                              "&:hover": {
                                background: "rgba(0, 206, 209, 0.15)",
                              },
                            }}
                          >
                            <Icon sx={{ color: "#00CED1", fontSize: "32px !important", mb: 1 }}>
                              qr_code_2
                            </Icon>
                            <VuiTypography variant="button" color="white" fontWeight="medium">
                              Show QR
                            </VuiTypography>
                          </VuiBox>
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <VuiBox display="flex" alignItems="center" justifyContent="center" p={3}>
                      <VuiTypography variant="button" color="error">
                        No wallet loaded
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6} xl={7}>
              <VuiBox mb={3}>
                <Card>
                  <VuiBox sx={{ height: "100%" }}>
                    <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                      Wallet Chart
                    </VuiTypography>
                    <VuiBox display="flex" alignItems="center" mb="40px">
                      <VuiTypography variant="button" color="success" fontWeight="bold">
                        Balance: {walletBalance.toFixed(2)} MYT
                        {mytPrice && (
                          <VuiTypography variant="button" color="text" fontWeight="regular">
                            {" "}≈ {currencyService.formatAmount(walletBalance * mytPrice)}
                          </VuiTypography>
                        )}
                        <VuiTypography variant="button" color="text" fontWeight="regular">
                          {" "} ({walletUtxos.length} UTXOs)
                        </VuiTypography>
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox sx={{ height: "310px" }}>
                      {balanceChartData.length > 0 ? (
                        <LineChart
                          lineChartData={balanceChartData}
                          lineChartOptions={balanceChartOptions}
                        />
                      ) : (
                        <VuiBox display="flex" alignItems="center" justifyContent="center" height="100%">
                          <VuiTypography variant="button" color="text">
                            Loading balance history...
                          </VuiTypography>
                        </VuiBox>
                      )}
                    </VuiBox>
                  </VuiBox>
                </Card>
              </VuiBox>
              <Projects title="Last Transactions" transactions={transactions} />
            </Grid>
            <Grid item xs={12} lg={6} xl={5}>
              <VuiBox mb={3}>
                <Card>
                  <VuiBox p={3}>
                    <VuiBox display="flex" alignItems="center" mb="16px">
                      <Icon sx={{ color: "#00CED1", fontSize: "36px", mr: "12px" }}>
                        analytics
                      </Icon>
                      <VuiBox>
                        <VuiTypography variant="lg" color="white" fontWeight="bold">
                          Blockchain Statistics
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                  
                  {/* Latest Blocks */}
                  <VuiBox mb="20px">
                    {loading ? (
                      <VuiBox display="flex" alignItems="center" justifyContent="center" p={3}>
                        <VuiTypography variant="button" color="text">
                          Loading blocks...
                        </VuiTypography>
                      </VuiBox>
                    ) : latestBlocks.length > 0 ? (
                      <Stack spacing={1.5}>
                        {latestBlocks.map((block, index) => (
                          <VuiBox
                            key={index}
                            sx={{
                              background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
                              borderRadius: "12px",
                              p: 2,
                              border: "1px solid transparent",
                              backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
                              backgroundOrigin: "border-box",
                              backgroundClip: "padding-box, border-box",
                              "&:hover": {
                                background: "linear-gradient(135deg, rgba(0, 117, 121, 0.3) 0%, rgba(75, 23, 126, 0.3) 100%)",
                                backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.3) 0%, rgba(75, 23, 126, 0.3) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.8) 0%, rgba(138, 43, 226, 0.8) 100%)",
                                backgroundOrigin: "border-box",
                                backgroundClip: "padding-box, border-box",
                              },
                            }}
                          >
                            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <VuiBox display="flex" alignItems="center">
                                <Icon sx={{ color: "#00CED1", fontSize: "20px", mr: 1 }}>
                                  view_in_ar
                                </Icon>
                                <VuiTypography color="white" variant="button" fontWeight="bold">
                                  Block #{block.height}
                                </VuiTypography>
                              </VuiBox>
                              <VuiTypography color="text" variant="caption">
                                {timeAgo(block.time)}
                              </VuiTypography>
                            </VuiBox>
                            <VuiBox display="flex" justifyContent="space-between">
                              <VuiTypography color="text" variant="caption">
                                {block.txCount} transaction{block.txCount !== 1 ? 's' : ''}
                              </VuiTypography>
                              <VuiTypography 
                                color="info" 
                                variant="caption" 
                                sx={{ fontFamily: 'monospace', fontSize: '10px' }}
                              >
                                {block.hash.substring(0, 12)}...
                              </VuiTypography>
                            </VuiBox>
                          </VuiBox>
                        ))}
                      </Stack>
                    ) : (
                      <VuiBox display="flex" alignItems="center" justifyContent="center" p={3}>
                        <VuiTypography variant="button" color="text">
                          No blocks available
                        </VuiTypography>
                      </VuiBox>
                    )}
                  </VuiBox>
                  
                  {/* Network Status Box */}
                  <VuiBox 
                    mb="20px" 
                    p={2}
                    sx={{
                      background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
                      borderRadius: "12px",
                      border: "1px solid transparent",
                      backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
                      backgroundOrigin: "border-box",
                      backgroundClip: "padding-box, border-box"
                    }}
                  >
                    <VuiBox display="flex" justifyContent="space-between" mb="8px">
                      <VuiTypography variant="button" color="text" fontWeight="regular">
                        Network Status
                      </VuiTypography>
                      <VuiTypography 
                        variant="button" 
                        color={networkStats.status === 'Online' ? 'success' : 'error'} 
                        fontWeight="bold"
                      >
                        {networkStats.status}
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox display="flex" justifyContent="space-between">
                      <VuiTypography variant="button" color="text" fontWeight="regular">
                        Current Block Height
                      </VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {networkStats.blockHeight}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={3} lg={3}>
                      <VuiBox display="flex" flexDirection="column" alignItems="center">
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ borderRadius: "6px", width: "25px", height: "25px", mb: 1 }}
                        >
                          <IoWallet color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography color="text" variant="button" fontWeight="medium" mb={1} textAlign="center">
                          Circ. Supply
                        </VuiTypography>
                        <VuiTypography color="white" variant="lg" fontWeight="bold" textAlign="center">
                          {networkStats.moneySupply && networkStats.moneySupply !== "N/A" && !isNaN(networkStats.moneySupply) 
                            ? `${parseInt(networkStats.moneySupply).toLocaleString()} MYT` 
                            : 'Loading...'}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <VuiBox display="flex" flexDirection="column" alignItems="center">
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ borderRadius: "6px", width: "25px", height: "25px", mb: 1 }}
                        >
                          <IoIosRocket color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography color="text" variant="button" fontWeight="medium" mb={1} textAlign="center">
                          Hashrate
                        </VuiTypography>
                        <VuiTypography color="white" variant="lg" fontWeight="bold" textAlign="center">
                          {networkStats.hashrate ? `${(networkStats.hashrate / 1000000000).toFixed(2)} GH/s` : 'N/A'}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <VuiBox display="flex" flexDirection="column" alignItems="center">
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ borderRadius: "6px", width: "25px", height: "25px", mb: 1 }}
                        >
                          <IoGlobe color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography color="text" variant="button" fontWeight="medium" mb={1} textAlign="center">
                          Network Peers
                        </VuiTypography>
                        <VuiTypography color="white" variant="lg" fontWeight="bold" textAlign="center">
                          {networkStats.networkPeers || 0}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <VuiBox display="flex" flexDirection="column" alignItems="center">
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ borderRadius: "6px", width: "25px", height: "25px", mb: 1 }}
                        >
                          <IoBuild color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography color="text" variant="button" fontWeight="medium" mb={1} textAlign="center">
                          Difficulty
                        </VuiTypography>
                        <VuiTypography color="white" variant="lg" fontWeight="bold" textAlign="center">
                          {networkStats.difficulty ? (networkStats.difficulty / 1000000).toFixed(2) + 'M' : 'N/A'}
                        </VuiTypography>
                      </VuiBox>
                    </Grid>
                  </Grid>
                </VuiBox>
              </Card>
              </VuiBox>
              <OrderOverview />
            </Grid>
          </Grid>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
